// ================================================================
//  EditBizEventMappingsController
//
//  Quick-edit dialog for biz event field mappings ONLY.
//  Bypasses the full wizard — loads the existing version data,
//  renders the mapping step directive directly, and PUTs only
//  the mapping changes back to the server.
//
//  Locals injected:
//    - bizEvent  : parent event header  (biz_event_id, biz_event_name, …)
//    - version   : the specific version object
//    - profile   : current profile
// ================================================================

angular.module('bizmetryApp').controller('EditBizEventMappingsController', function (
    $scope,
    $rootScope,
    $mdDialog,
    $mdToast,
    $timeout,
    ApiCollectionService,
    BusinessEventsService,
    ProfileService,
    TemplateInstanceService,
    BmelEditorService,
    dialogService,
    bizEvent,
    version,
    profile
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    // ── State ────────────────────────────────────────────────────────────────
    ctrl.bizEvent        = bizEvent;
    ctrl.versionData     = version;
    ctrl.loadingVersion  = true;
    ctrl.saving          = false;
    ctrl.hasChanges      = false;
    ctrl.bmelErrors      = false;
    ctrl.errorMessage    = null;

    // ── Mapping directive bindings ────────────────────────────────────────────
    ctrl.selectedOperations = [];
    ctrl.selectedFrameType  = null;
    ctrl.templateFull       = null;
    ctrl.mappings           = [];
    ctrl.targetExpressions  = {};
    ctrl.preloadMappings    = null;

    // Snapshot for dirty tracking
    ctrl._originalMappingCount = (version.biz_event_mappings || []).length;
    ctrl._mappingsDirty        = false;

    // ── Header display info ───────────────────────────────────────────────────
    ctrl.eventName    = bizEvent.biz_event_name || '—';
    ctrl.versionLabel = 'v' + (version.biz_event_version || '');
    ctrl.statusLabel  = version.biz_event_status || '';

    // ── Dirty tracking ────────────────────────────────────────────────────────
    // _preloadSettled: false until the directive has finished writing the
    // translated preload into ctrl.mappings and ctrl.targetExpressions.
    // While false we ignore all watcher fires — they are the preload, not edits.
    ctrl._preloadSettled = false;

    // $watchCollection detects add/remove connectors (directive replaces or
    // pushes into the array). Deep $watch also catches internal object mutations
    // (e.g. bmelExpression changes inside a mapping object).
    $scope.$watch('ctrl.mappings', function (n, o) {
        if (!ctrl._preloadSettled) { return; }
        ctrl._mappingsDirty = true;
        _recomputeDirty();
    }, true);

    // targetExpressions is mutated in-place (ctrl.targetExpressions[key] = val).
    // Deep watch fires on any key add/change/remove. Note: n === o by reference
    // (same object), so we never guard on n !== o here — any fire after settle
    // means the user edited an expression.
    $scope.$watch('ctrl.targetExpressions', function (n, o) {
        if (!ctrl._preloadSettled) { return; }
        ctrl._mappingsDirty = true;
        _recomputeDirty();
    }, true);

    function _recomputeDirty() {
        ctrl.hasChanges = ctrl._mappingsDirty;
    }

    // ── Required components validation (mirrors EditBizEventVersionController) ─
    ctrl.allRequiredComponentsMapped = function () {
        if (!ctrl.selectedFrameType) { return false; }
        var comps = ctrl.selectedFrameType.frame_fields    ||
                    ctrl.selectedFrameType.components      ||
                    ctrl.selectedFrameType.frame_components || [];
        var required = comps.filter(function (c) { return c.is_required; });
        if (required.length === 0) { return true; }
        return required.every(function (c) {
            var compId = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
            return (ctrl.mappings || []).some(function (m) { return m.targetComponentId === compId; });
        });
    };

    ctrl.unmappedRequiredCount = function () {
        if (!ctrl.selectedFrameType) { return 0; }
        var comps = ctrl.selectedFrameType.frame_fields    ||
                    ctrl.selectedFrameType.components      ||
                    ctrl.selectedFrameType.frame_components || [];
        return comps.filter(function (c) {
            if (!c.is_required) { return false; }
            var compId = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
            return !(ctrl.mappings || []).some(function (m) { return m.targetComponentId === compId; });
        }).length;
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    ctrl.save = function () {
        if (!ctrl.hasChanges || ctrl.saving || ctrl.bmelErrors || !ctrl.allRequiredComponentsMapped()) { return; }
        ctrl.saving = true;
        ctrl.errorMessage = null;

        var payload = _buildFullUpdatePayload(_buildMappingsPayload());

        BusinessEventsService.update(bizEvent.biz_event_id, payload)
            .then(function () {
                $mdToast.show(
                    $mdToast.simple()
                        .textContent('Mappings updated successfully')
                        .position('top right')
                        .hideDelay(3000)
                );
                $mdDialog.hide({ updated: true });
            })
            .catch(function (err) {
                console.error('[EditBizEventMappings] Save failed:', err);
                ctrl.errorMessage = (err.data && err.data.message) || 'Could not save mappings. Please try again.';
            })
            .finally(function () { ctrl.saving = false; });
    };

    // ── Cancel ────────────────────────────────────────────────────────────────
    ctrl.cancel = function () {
        if (!ctrl.hasChanges) { $mdDialog.cancel(); return; }
        dialogService.showDecisionDialog(
            'Discard Changes',
            'You have unsaved mapping changes. Are you sure you want to close without saving?'
        ).then(function (decision) {
            if (decision === 'proceed') { $mdDialog.cancel(); }
        });
    };

    // ── _buildMappingsPayload ─────────────────────────────────────────────────
    // Mirrors EditBizEventVersionController._buildMappingsPayload()
    function _buildMappingsPayload() {
        var result = [];
        if (!ctrl.selectedFrameType) { return result; }

        var frameComps = ctrl.selectedFrameType.frame_fields    ||
                         ctrl.selectedFrameType.components      ||
                         ctrl.selectedFrameType.frame_components || [];

        function _findFrameComp(compId) {
            return frameComps.find(function (c) {
                var cid = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
                return String(cid) === String(compId);
            }) || null;
        }

        function _makeSource(src) {
            var opEntry = ctrl.selectedOperations[src.opIdx];
            if (!opEntry) { return null; }
            var backendSourceType = src.sourceType;
            if (src.sourceType === 'CONTEXT') {
                var p = src.path || '';
                backendSourceType = (p.indexOf('$bm.response') === 0)
                    ? 'RESPONSE_CONTEXT'
                    : 'REQUEST_CONTEXT';
            }
            return {
                operation_name: opEntry.op.operationName || opEntry.op.summary || opEntry.op.path,
                source_type:    backendSourceType,
                source_path:    src.path,
                placeholder:    null
            };
        }

        ctrl.mappings.forEach(function (m) {
            var mappingSource = _makeSource(m.source);
            if (!mappingSource) { return; }

            var fc       = _findFrameComp(m.targetComponentId);
            var compType = (fc && (fc.frame_component_type || fc.component_type)) || 'FIELD_REFERENCE';
            var compId   = fc ? (fc.frame_component_id || fc.id || fc.component_id || null) : null;
            var attrId   = fc ? (fc.attribute_type_id   || null) : null;
            var attrName = fc ? (fc.attribute_type_name || null) : null;
            var expr     = ctrl.targetExpressions[m.targetComponentId] || m.bmelExpression || null;

            result.push({
                sources: [mappingSource],
                target: {
                    component_id:   compId,
                    component_name: m.targetComponentName,
                    component_type: compType,
                    attribute_id:   attrId,
                    attribute_name: attrName
                },
                transform: expr ? { expression: expr } : null
            });

            if (m.columnMappings) {
                Object.keys(m.columnMappings).forEach(function (colName) {
                    var cm = m.columnMappings[colName];
                    if (!cm || !cm.source) { return; }
                    var colSource = _makeSource(cm.source);
                    if (!colSource) { return; }
                    var colExpr = ctrl.targetExpressions[m.targetComponentId + '|col|' + colName]
                                  || cm.bmelExpression || null;
                    result.push({
                        sources: [colSource],
                        target: {
                            component_id:   compId,
                            component_name: m.targetComponentName,
                            component_type: 'NESTED_TABLE',
                            attribute_id:   attrId,
                            attribute_name: colName
                        },
                        transform: colExpr ? { expression: colExpr } : null
                    });
                });
            }
        });

        return result;
    }

    // ── _buildFullUpdatePayload ───────────────────────────────────────────────
    function _buildFullUpdatePayload(mappingsPayload) {
        var v = ctrl._fullVersion || version;

        var sources = ctrl.selectedOperations.map(function (s) {
            var sourceType = (v.biz_event_sources && v.biz_event_sources[0])
                ? v.biz_event_sources[0].source_type
                : 'API';
            return {
                source_type:             sourceType,
                event_interception_mode: s.interceptionMode,
                cardinality:             s.cardinality || 'ONE',
                api_collection_id:       s.collection.collection_id,
                api_collection_name:     s.collection.collection_name,
                api_collection_version:  s.collection.api_version || null,
                operation_id:            s.op.operationId,
                operation_name:          s.op.operationName || s.op.summary || s.op.path,
                api_method:              s.op.method,
                api_endpoint:            s.op.path,
                api_signature:           s.op.signature || s.op.path,
                correlation_key: {
                    source_type: s.correlationKey.sourceType,
                    source_path: s.correlationKey.sourcePath
                },
                filters: (s.filters || [])
                    .filter(function (f) {
                        return f.filterType && f.sourcePath && f.sourcePath.trim() && f.allowedValues.length > 0;
                    })
                    .map(function (f) {
                        return {
                            filter_type:    f.filterType,
                            source_path:    f.sourcePath.trim(),
                            allowed_values: f.allowedValues,
                            case_sensitive: f.caseSensitive
                        };
                    })
            };
        });

        return {
            biz_event_name:        bizEvent.biz_event_name,
            biz_event_description: bizEvent.biz_event_description || '',
            account_id:            profile.accountId || profile.account_id,
            profile_id:            profile.id,
            biz_event_version:     version.biz_event_version,
            biz_event_trigger: v.biz_event_trigger
                ? {
                    biz_event_trigger_id:          v.biz_event_trigger.biz_event_trigger_id,
                    biz_event_trigger_description: v.biz_event_trigger.biz_event_trigger_description || ''
                  }
                : null,
            biz_event_sources:  sources,
            biz_event_target:   v.biz_event_target
                ? {
                    template_instance_id:      v.biz_event_target.template_instance_id,
                    template_instance_version: v.biz_event_target.template_instance_version,
                    frame_type_id:             v.biz_event_target.frame_type_id,
                    frame_type_name:           v.biz_event_target.frame_type_name
                  }
                : null,
            biz_event_mappings: mappingsPayload
        };
    }

    // ── _convertApiMappingsToIntelliSenseFormat ───────────────────────────────
    // Exact copy of EditBizEventVersionController._convertApiMappingsToIntelliSenseFormat().
    // Converts backend biz_event_mappings → the IS shape that
    // _translateIntelliSenseMappings() inside the directive expects.
    //
    // KEY INVARIANT: src.operation_id in the output MUST equal
    //   op.operationId (or op.operation_id) of the matching selectedOperation,
    //   because _translateIntelliSenseMappings matches by:
    //     String(op.operationId) === String(src.operation_id)
    function _convertApiMappingsToIntelliSenseFormat(apiMappings) {
        if (!apiMappings || !apiMappings.length) { return []; }

        // Index selectedOperations by every name variant stored on the op object.
        // The backend biz_event_mappings.sources[].operation_name holds whatever
        // was written at save time:  op.operationName || op.summary || op.path
        // So we must index all three to guarantee a hit.
        var opNameToIdx = {};
        ctrl.selectedOperations.forEach(function (entry, idx) {
            var op = entry.op;
            [
                op.operationName,   // set from op.operation_name (metadata) or op.summary (swagger)
                op.operationId,     // set from op.operation_id  (metadata) or op.operationId (swagger)
                op.summary,
                op.path
            ].filter(Boolean).forEach(function (n) {
                if (!opNameToIdx.hasOwnProperty(n)) { opNameToIdx[n] = idx; }
            });
        });

        var componentMap   = {};
        var componentOrder = [];

        apiMappings.forEach(function (entry) {
            var tgt      = entry.target   || {};
            var transform = entry.transform || {};
            var compId   = tgt.component_id;
            var compName = tgt.component_name;
            var compType = tgt.component_type || 'FIELD_REFERENCE';
            var attrName = tgt.attribute_name;
            var bmelExpr = transform.expression || null;

            if (!compId) { return; }

            (entry.sources || []).forEach(function (src) {
                var opName  = src.operation_name || '';
                var opIdx   = opNameToIdx.hasOwnProperty(opName) ? opNameToIdx[opName] : -1;
                var opEntry = opIdx >= 0 ? ctrl.selectedOperations[opIdx] : null;

                // operation_id in the IS output must be the value that
                // _translateIntelliSenseMappings can match against op.operationId.
                // When opEntry is available use the real operationId from the op
                // object; fall back to opName only when the op couldn't be resolved
                // (the directive will also discard it in that case, same as the wizard).
                var operationId = opEntry
                    ? (opEntry.op.operationId || opEntry.op.operationName || opName)
                    : opName;

                if (compType === 'NESTED_TABLE' && attrName) {
                    if (!componentMap[compId]) {
                        componentMap[compId] = {
                            frame_component_id: compId,
                            frame_field_name:   compName,
                            sources:            [],
                            bmel_expression:    null
                        };
                        componentOrder.push(compId);
                    }
                    var parentEntry = componentMap[compId];
                    var parentSrc = parentEntry.sources.find(function (s) {
                        return String(s.operation_id) === String(operationId);
                    });
                    if (!parentSrc) {
                        parentSrc = {
                            operation_id:    operationId,
                            source_type:     src.source_type,
                            field_path:      src.source_path || '',
                            field_name:      null,
                            column_mappings: []
                        };
                        parentEntry.sources.push(parentSrc);
                    }
                    if (!parentSrc.column_mappings) { parentSrc.column_mappings = []; }
                    parentSrc.column_mappings.push({
                        column_name:     attrName,
                        // Columns of a MANY-operation NESTED_TABLE can come from any
                        // section of that operation, so keep the column's own type.
                        source_type:     src.source_type,
                        field_path:      src.source_path || '',
                        field_name:      null,
                        bmel_expression: bmelExpr
                    });

                } else {
                    if (!componentMap[compId]) {
                        componentMap[compId] = {
                            frame_component_id: compId,
                            frame_field_name:   compName,
                            sources:            [],
                            bmel_expression:    null
                        };
                        componentOrder.push(compId);
                    }
                    var isEntry = componentMap[compId];
                    isEntry.bmel_expression = isEntry.bmel_expression || bmelExpr;
                    isEntry.sources.push({
                        operation_id:    operationId,
                        source_type:     src.source_type,
                        field_path:      src.source_path || '',
                        field_name:      null,
                        column_mappings: []
                    });
                }
            });
        });

        return componentOrder.map(function (compId) { return componentMap[compId]; });
    }

    // ── Op reconstruction helpers ─────────────────────────────────────────────
    // IMPORTANT: field names must exactly mirror EditBizEventVersionController
    // so the opNameToIdx lookup and _translateIntelliSenseMappings matching work.
    //
    // From metadata (col.operations[] — already parsed by the backend):
    //   op.operation_id   → operationId
    //   op.operation_name → operationName
    //   op.summary        → summary
    //
    // _translateIntelliSenseMappings matches by:
    //   String(op.operationId || op.operation_id || op.id) === String(src.operation_id)
    //
    // opNameToIdx indexes by [operationName, operationId, summary, path].
    // The backend writes operation_name = operationName||summary||path at save time,
    // so at least one of those will hit.

    function _opsFromMetadata(ops) {
        if (!ops || !ops.length) { return []; }
        return ops.map(function (op, i) {
            return {
                _index:        i,
                _pathKey:      op.path,
                _method:       (op.method || '').toLowerCase(),
                method:        (op.method || '').toUpperCase(),
                path:          op.path,
                operationId:   op.operation_id   || null,   // ← snake_case from backend metadata
                operationName: op.operation_name || null,   // ← snake_case from backend metadata
                summary:       op.summary        || null,
                description:   op.description    || null,
                tags:          op.tags           || [],
                signature:     op.signature      || op.path,
                queryParams:   op.query_params   || [],
                uriParams:     op.uri_params     || [],
                httpHeaders:   op.http_headers   || [],
                payload:       op.payload        || null
            };
        });
    }

    function _parseOperations(swagger) {
        if (!swagger) { return []; }
        var spec   = typeof swagger === 'string' ? JSON.parse(swagger) : swagger;
        var result = [];
        var index  = 0;
        var paths  = spec.paths || {};
        var HTTP_METHODS = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'];
        Object.keys(paths).forEach(function (path) {
            var pathItem = paths[path];
            HTTP_METHODS.forEach(function (method) {
                if (!pathItem[method]) { return; }
                var op = pathItem[method];
                result.push({
                    _index:        index++,
                    _pathKey:      path,
                    _method:       method,
                    method:        method.toUpperCase(),
                    path:          path,
                    operationId:   op.operationId  || null,
                    operationName: op.summary      || op.operationId || null,
                    summary:       op.summary      || null,
                    description:   op.description  || null,
                    tags:          op.tags         || [],
                    signature:     path,
                    queryParams:   [],
                    uriParams:     [],
                    httpHeaders:   [],
                    payload:       null
                });
            });
        });
        return result;
    }

    // ── IntelliSense still at work after a recompute ──────────────────────────
    // A template / API change left targets unmapped and IntelliSense (~1 min) is mapping them:
    // say so, and when it finishes reload in place — or, with unsaved edits, offer to.
    ctrl.intelliSensePendingSince = null;
    ctrl.intelliSenseUpdateReady  = false;
    var _stopIntelliSenseWatch = angular.noop;

    function _watchIntelliSense(fullVersion) {
        _stopIntelliSenseWatch();
        ctrl.intelliSensePendingSince = BusinessEventsService.intelliSensePendingSince(fullVersion);
        if (!ctrl.intelliSensePendingSince) { return; }
        _stopIntelliSenseWatch = BusinessEventsService.watchIntelliSenseRecompute(
            bizEvent.biz_event_id, version.biz_event_version_id, function () {
                ctrl.intelliSensePendingSince = null;
                if (ctrl.hasChanges) {
                    ctrl.intelliSenseUpdateReady = true;
                } else {
                    _reload('IntelliSense finished — mappings updated');
                }
            });
    }
    $scope.$on('$destroy', function () { _stopIntelliSenseWatch(); });

    ctrl.intelliSensePendingAgo = function () {
        if (!ctrl.intelliSensePendingSince) { return ''; }
        var secs = Math.max(0, Math.round((Date.now() - ctrl.intelliSensePendingSince) / 1000));
        return secs < 60 ? secs + 's ago' : Math.floor(secs / 60) + ' min ago';
    };

    /** Banner action once IntelliSense finished while there were unsaved edits. */
    ctrl.reloadIntelliSenseMappings = function () {
        dialogService.showDecisionDialog(
            'Load IntelliSense Mappings',
            'Reloading shows the mappings IntelliSense just added and discards your unsaved changes. Continue?'
        ).then(function (decision) {
            if (decision === 'proceed') { _reload('Mappings reloaded'); }
        });
    };

    function _reload(toast) {
        ctrl.intelliSenseUpdateReady = false;
        ctrl.loadingVersion     = true;    // ng-if drops the mapping directive; _load re-creates it
        ctrl.errorMessage       = null;
        ctrl.selectedOperations = [];
        ctrl.mappings           = [];
        ctrl.targetExpressions  = {};
        ctrl.preloadMappings    = null;
        ctrl._preloadSettled    = false;
        ctrl._mappingsDirty     = false;
        ctrl.hasChanges         = false;
        _load().then(function () {
            if (toast) {
                $mdToast.show($mdToast.simple().textContent(toast).position('top right').hideDelay(3000));
            }
        });
    }

    // ── INITIALIZATION ────────────────────────────────────────────────────────

    function _load() {
    return BusinessEventsService.getById(bizEvent.biz_event_id)
        .then(function (response) {
            var fullEvent = response.data;
            if (!fullEvent) { throw new Error('Empty response'); }

            var fullVersion = null;
            if (fullEvent.versions && fullEvent.versions.length) {
                fullVersion = fullEvent.versions.find(function (v) {
                    return v.biz_event_version_id === version.biz_event_version_id;
                });
            }
            if (!fullVersion) { fullVersion = version; }
            ctrl._fullVersion = fullVersion;
            _watchIntelliSense(fullVersion);

            var target   = fullVersion.biz_event_target  || {};
            var sources  = fullVersion.biz_event_sources  || [];
            var mappings = fullVersion.biz_event_mappings || [];

            // ── Step A: reconstruct selectedOperations ────────────────────
            var colIds = [];
            sources.forEach(function (src) {
                if (src.api_collection_id && colIds.indexOf(src.api_collection_id) === -1) {
                    colIds.push(src.api_collection_id);
                }
            });

            var opsPromise = (colIds.length === 0)
                ? Promise.resolve()
                : ApiCollectionService.getAll(null, {
                      page: 0, size: 200, sortField: 'collection_name', sortDir: 'ASC'
                  })
                    .then(function (res) {
                        // Any non-retired collection — biz events may use ones still in progress.
                        var allCols = ((res.data && res.data.content) || [])
                            .filter(function (c) { return c.status !== 'RETIRED' && c.status !== 'DECOMMISSIONED'; });
                        var colsToLoad = allCols.filter(function (c) {
                            return colIds.indexOf(c.collection_id) !== -1;
                        });
                        return Promise.all(colsToLoad.map(function (col) {
                            return ApiCollectionService.getById(col.collection_id)
                                .then(function (r) { return r.data || r; });
                        }));
                    })
                    .then(function (loadedCols) {
                        var colMap = {};
                        loadedCols.forEach(function (col) { colMap[col.collection_id] = col; });

                        var addedKeys = {};
                        sources.forEach(function (src) {
                            var col = colMap[src.api_collection_id];
                            if (!col) { return; }

                            // Use _opsFromMetadata when the collection has pre-parsed
                            // operations (col.operations[]), otherwise parse swagger.
                            // _opsFromMetadata correctly maps snake_case backend fields
                            // (operation_id, operation_name) to camelCase (operationId,
                            // operationName) that _convertApiMappingsToIntelliSenseFormat
                            // and _translateIntelliSenseMappings both rely on.
                            var ops = col.operations
                                ? _opsFromMetadata(col.operations)
                                : _parseOperations(col.swagger);

                            // Match the operation saved in biz_event_sources
                            var matchedOp = ops.find(function (op) {
                                if (src.operation_id && op.operationId) {
                                    return String(op.operationId) === String(src.operation_id);
                                }
                                return op.method === (src.api_method || '').toUpperCase() &&
                                       op.path   === src.api_endpoint;
                            });
                            if (!matchedOp) { return; }

                            var opKey = src.api_collection_id + '|' + matchedOp.path + '|' + matchedOp.method;
                            if (addedKeys[opKey]) { return; }
                            addedKeys[opKey] = true;

                            ctrl.selectedOperations.push({
                                op:               matchedOp,
                                collection:       col,
                                interceptionMode: src.event_interception_mode || 'INBOUND',
                                cardinality:      src.cardinality || 'ONE',
                                correlationKey: {
                                    sourceType: src.correlation_key ? src.correlation_key.source_type : null,
                                    sourcePath: src.correlation_key ? src.correlation_key.source_path  : ''
                                },
                                filters: (src.filters || []).map(function (f) {
                                    return {
                                        filterType:    f.filter_type,
                                        sourcePath:    f.source_path,
                                        allowedValues: f.allowed_values || [],
                                        caseSensitive: f.case_sensitive !== false,
                                        _newValue:     '',
                                        _open:         false
                                    };
                                }),
                                _optionsByType: {},
                                _configOpen:    false
                            });
                        });
                    });

            // ── Step B: load template → get frame_type with frame_fields ──
            var tplPromise = target.template_instance_id
                ? TemplateInstanceService.getTemplateInstanceById(target.template_instance_id)
                    .then(function (tplRes) {
                        var full       = tplRes.data || tplRes;
                        ctrl.templateFull = full;
                        var frameTypes = (full.templateConfig && full.templateConfig.frame_types) || [];
                        var matchedFt  = frameTypes.find(function (ft) {
                            return String(ft.frame_type_id || ft.id) === String(target.frame_type_id);
                        });
                        ctrl.selectedFrameType = matchedFt || {
                            frame_type_id:    target.frame_type_id,
                            frame_name:       target.frame_type_name,
                            frame_type_name:  target.frame_type_name,
                            frame_fields:     [],
                            components:       [],
                            frame_components: []
                        };
                    })
                    .catch(function (err) {
                        console.warn('[EditBizEventMappings] Could not load template:', err);
                        ctrl.selectedFrameType = {
                            frame_type_id:    target.frame_type_id,
                            frame_name:       target.frame_type_name,
                            frame_type_name:  target.frame_type_name,
                            frame_fields:     [],
                            components:       [],
                            frame_components: []
                        };
                    })
                : Promise.resolve();

            // ── Step C: reveal directive then feed preload ────────────────
            // bmelPromise: load Monaco + BMEL function registry in parallel
            // with the data loads so the tokenizer has KNOWN_FN_NAMES populated
            // before the first editor opens. Without this, Monaco registers the
            // BMEL language with an empty function list → functions get red
            // underlines on first open and only resolve on the second open
            // (after loadFunctions() has been called by some other code path).
            var bmelPromise = Promise.all([
                BmelEditorService.loadMonaco(),
                BmelEditorService.getFunctions().length
                    ? Promise.resolve()          // already loaded — skip the round-trip
                    : BmelEditorService.loadFunctions()
            ]).then(function () {
                BmelEditorService.refreshLanguageTokenizer();
            }).catch(angular.noop);  // non-fatal — editor still opens, just without highlighting

            return Promise.all([opsPromise, tplPromise, bmelPromise]).then(function () {
                var converted = _convertApiMappingsToIntelliSenseFormat(mappings);
                ctrl._originalMappingCount = mappings.length;

                // 1. Drop the loading overlay — ng-if on selectedFrameType is now
                //    true, so the directive enters the DOM.
                ctrl.loadingVersion = false;

                // 2. Next tick: directive's $onInit has run, DOM has real pixel
                //    dimensions → safe to feed preloadMappings so the directive's
                //    $watch fires into a fully painted container.
                $timeout(function () {
                    ctrl.preloadMappings = converted;

                    // 3. Two ticks after preload: the directive's $watch + the deep
                    //    watchers on ctrl.mappings and ctrl.targetExpressions have all
                    //    fired and stabilised. Only then enable dirty tracking so that
                    //    the preload writes don't register as user edits.
                    $timeout(function () {
                        $timeout(function () {
                            ctrl._preloadSettled = true;
                        }, 0);
                    }, 0);
                }, 0);
            });
        })
        .catch(function (err) {
            console.error('[EditBizEventMappings] Load error:', err);
            ctrl.errorMessage = 'Failed to load mapping data. Please close and try again.';
            ctrl.loadingVersion = false;
        });
    }

    _load();

});